"use client";

import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { capture, captureBilling, isPostHogExcludedPath, resumePostHogCapturing, silencePostHogForExcludedPath } from "@/lib/analytics";

/**
 * Manual SPA pageview tracking. App Router navigates on the client, so PostHog's
 * automatic pageview (disabled via capture_pageview:false) would miss every
 * in-app navigation. We fire $pageview on each pathname change, including the
 * first mount. Uses usePathname only (not useSearchParams) to avoid forcing a
 * Suspense boundary; PostHog reads the full URL from window.location at capture.
 * Internal /metrics is excluded so admin refreshes do not pollute analytics.
 */
export function PostHogPageview() {
  const pathname = usePathname();
  const last = useRef<string | null>(null);

  useEffect(() => {
    if (!pathname || last.current === pathname) return;
    last.current = pathname;
    if (isPostHogExcludedPath(pathname)) {
      silencePostHogForExcludedPath();
      return;
    }
    resumePostHogCapturing();
    capture("$pageview");
    if (pathname === "/pricing" || /^\/[a-z-]+\/pricing$/.test(pathname)) captureBilling("pricing_viewed", { source: "pricing" });
  }, [pathname]);

  useEffect(() => {
    function onClick(event: MouseEvent) {
      const link = event.target instanceof Element ? event.target.closest<HTMLElement>("[data-billing-offer]") : null;
      if (!link) return;
      captureBilling("billing_offer_clicked", { offer: link.dataset.billingOffer, source: link.dataset.billingSource }, link.dataset.billingPersist === "true");
    }
    document.addEventListener("click", onClick, { capture: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
